import { ContractPromise } from '@polkadot/api-contract';
import { useQuery } from '@tanstack/react-query';
import { BigNumber } from 'bignumber.js';
import { readContract } from 'wagmi/actions';
import type { WebsiteSupportedChains } from '@/lib/constants';
import { D9_USDT_ABI, EVM_USDT_ABI, TRON_USDT_ABI } from '@/lib/constants';
import { getD9Api, getGasLimits } from '@/lib/d9-api';
import { clientEnv } from '@/lib/env';
import { parseD9Address } from '@/lib/utils/d9';
import { wagmiConfig } from '@/lib/wagmi';

/**
 * Hook to get USDT balance for a given chain and address
 * @param chain - The chain to query (BSC, TRON, or D9)
 * @param address - The address to query balance for
 * @returns Query result with balance as a string
 */
export function useBalance(
  chain: WebsiteSupportedChains,
  address: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: ['usdt-balance', chain, address],
    queryFn: async () => {
      if (!address) {
        return 0;
      }

      const d9Api = await getD9Api();

      try {
        switch (chain) {
          case 'BSC': {
            const balance = await readContract(wagmiConfig, {
              address:
                clientEnv.VITE_BSC_USDT_CONTRACT_ADDRESS as `0x${string}`,
              abi: EVM_USDT_ABI,
              functionName: 'balanceOf',
              args: [address as `0x${string}`],
            });
            const decimals = await readContract(wagmiConfig, {
              address:
                clientEnv.VITE_BSC_USDT_CONTRACT_ADDRESS as `0x${string}`,
              abi: EVM_USDT_ABI,
              functionName: 'decimals',
            });
            // BSC USDT has 18 decimals
            const balanceBN = BigNumber(balance.toString())
              .div(BigNumber(10).pow(BigNumber(decimals.toString())))
              .toNumber();

            return balanceBN.valueOf();
          }

          case 'TRON': {
            if (!window.tronWeb) {
              return 0;
            }
            const contract = window.tronWeb.contract(
              TRON_USDT_ABI,
              clientEnv.VITE_TRON_USDT_CONTRACT_ADDRESS,
            );
            const decimals = BigNumber(await contract.decimals().call());
            const balance = await contract.balanceOf(address).call();
            const balanceBN = BigNumber(balance.toString())
              .div(BigNumber(10).pow(BigNumber(decimals.toString())))
              .toNumber();

            return balanceBN.valueOf();
          }

          case 'D9': {
            if (
              !clientEnv.VITE_D9_NODE_URL ||
              !clientEnv.VITE_D9_USDT_CONTRACT_ADDRESS
            ) {
              return 0;
            }

            try {
              address = parseD9Address(address);
            } catch {
              return 0;
            }

            try {
              const contract = new ContractPromise(
                d9Api,
                D9_USDT_ABI,
                clientEnv.VITE_D9_USDT_CONTRACT_ADDRESS,
              );

              const queryResult = await contract.query['psp22::balanceOf']!(
                address,
                { gasLimit: getGasLimits(d9Api).readLimit },
                address,
              );

              if (
                queryResult &&
                queryResult.result.isOk &&
                queryResult.output
              ) {
                const balance = (queryResult.output as any).asOk.toString();
                const balanceBN = BigNumber(balance)
                  .div(BigNumber(10).pow(BigNumber(2)))
                  .toNumber();

                return balanceBN.valueOf();
              }

              return 0;
            } finally {
              await d9Api.disconnect();
            }
          }

          default:
            return 0;
        }
      } catch (error) {
        console.error(`Error fetching balance for ${chain}:`, error);
        return 0;
      }
    },
    enabled: enabled && !!address,
    refetchInterval: 10000, // Refetch every 10 seconds
  });
}
